import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

const About = () => {
  return (
    <div className="w-full">
      {/* Hero Section */}
      <div className="px-8 md:px-16 py-24 max-w-5xl mx-auto text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <span className="text-rust font-semibold tracking-widest uppercase text-sm mb-4 block">Our Legacy</span>
          <h1 className="text-7xl md:text-8xl text-olive mb-10">NUESTRA HISTORIA</h1>
          <p className="text-xl leading-relaxed text-olive/90 mb-8 max-w-3xl mx-auto">
            Founded in 1998, La Casa was born from a simple desire: to share the authentic flavors of our family's kitchen with the community. We believe that great food starts with heritage, traditional techniques, and a lot of love.
          </p>
          <p className="text-xl leading-relaxed text-olive/90 max-w-3xl mx-auto">
            Every dish we serve is a tribute to our ancestors, crafted to bring people together around the table. From our daily handmade tortillas to our slow-roasted carnitas, we take pride in every detail.
          </p>
        </motion.div>
      </div>

      {/* Image Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12 px-8 md:px-16 max-w-7xl mx-auto mb-24">
        <motion.div 
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          className="relative h-[500px] overflow-hidden rounded-tl-[120px] rounded-br-[30px] shadow-2xl"
        >
          <img 
            src="https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?q=80&w=1200&auto=format&fit=crop" 
            alt="La Casa Restaurant Interior" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-olive/10"></div>
          <div className="absolute bottom-8 left-8 bg-sand/90 backdrop-blur-sm p-6 rounded-tr-3xl shadow-lg border-l-4 border-rust">
            <h4 className="font-display text-2xl text-olive mb-1">THE ATMOSPHERE</h4>
            <p className="text-xs tracking-wider opacity-70">SAN FRANCISCO, CA • EST. 1998</p>
          </div>
        </motion.div>
        <motion.div 
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative h-[500px] overflow-hidden rounded-tr-[120px] rounded-bl-[30px] shadow-2xl mt-12 md:mt-24"
        >
          <img 
            src="https://images.unsplash.com/photo-1556910103-1c02745aae4d?q=80&w=1200&auto=format&fit=crop" 
            alt="Fresh Mexican Ingredients" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-rust/10"></div>
          <div className="absolute top-8 right-8 bg-rust text-sand p-6 rounded-bl-3xl shadow-lg">
            <h4 className="font-display text-2xl mb-1">THE INGREDIENTS</h4>
            <p className="text-xs tracking-wider opacity-80 uppercase">FRESH • LOCAL • ORGANIC</p>
          </div>
        </motion.div>
      </div>

      {/* Philosophy Section - Updated with Light Sand Background for better contrast */}
      <div className="bg-sand-light border-y border-olive/10 py-28 px-8 md:px-16 relative overflow-hidden">
        <div className="absolute top-0 right-0 opacity-5 transform translate-x-1/3 -translate-y-1/3">
           <div className="w-[600px] h-[600px] border-[60px] border-olive rounded-full"></div>
        </div>
        
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-20">
            <span className="text-rust font-semibold tracking-widest uppercase text-sm mb-4 block">Our Values</span>
            <h2 className="text-6xl text-olive tracking-wider uppercase font-display">NUESTRA FILOSOFÍA</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-10">
            <div className="p-10 bg-sand border border-olive/5 rounded-tr-[50px] hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group">
              <div className="w-14 h-14 bg-rust/10 rounded-full flex items-center justify-center mb-8 group-hover:bg-rust transition-colors">
                <SafeIcon name="Heart" className="w-7 h-7 text-rust group-hover:text-sand" />
              </div>
              <h3 className="text-3xl font-display mb-4 text-rust">Family First</h3>
              <p className="text-base text-olive/80 leading-relaxed">We treat every guest like familia. When you walk through our doors, you enter our home. Every interaction is guided by warmth, tradition, and genuine care.</p>
            </div>
            <div className="p-10 bg-sand border border-olive/5 rounded-tr-[50px] hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group">
              <div className="w-14 h-14 bg-rust/10 rounded-full flex items-center justify-center mb-8 group-hover:bg-rust transition-colors">
                <SafeIcon name="Check" className="w-7 h-7 text-rust group-hover:text-sand" />
              </div>
              <h3 className="text-3xl font-display mb-4 text-rust">Quality</h3>
              <p className="text-base text-olive/80 leading-relaxed">We never cut corners. From hand-pressing our tortillas to slow-cooking our moles for 48 hours, excellence is the only standard we accept for our guests.</p>
            </div>
            <div className="p-10 bg-sand border border-olive/5 rounded-tr-[50px] hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group">
              <div className="w-14 h-14 bg-rust/10 rounded-full flex items-center justify-center mb-8 group-hover:bg-rust transition-colors">
                <SafeIcon name="Users" className="w-7 h-7 text-rust group-hover:text-sand" />
              </div>
              <h3 className="text-3xl font-display mb-4 text-rust">Community</h3>
              <p className="text-base text-olive/80 leading-relaxed">We proudly source from local farmers and give back to the neighborhood that supported us from day one. Our kitchen is a hub for local culture and connection.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;